import { Component, input, output, computed } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { SortDirection, SortOption } from './sort.utils';

@Component({
  selector: 'app-sort-dropdown',
  imports: [MatIcon, TranslocoPipe],
  template: `
    <div class="sort-bar">
      <div class="sort-pills">
        @for (opt of options(); track opt.labelKey; let i = $index) {
          <button
            class="sort-pill"
            [class.sort-pill--active]="selectedFieldIndex() === i"
            (click)="onPillClick(i)">
            <span class="sort-pill-label">{{ opt.labelKey | transloco }}</span>
            @if (selectedFieldIndex() === i) {
              <mat-icon class="sort-pill-arrow">
                {{ selectedDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </mat-icon>
            }
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    .sort-bar {
      display: flex;
      align-items: center;
    }

    .sort-pills {
      display: flex;
      gap: 6px;
    }

    .sort-pill {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 5px 12px;
      border: 1px solid rgba(var(--border-color-rgb), 0.15);
      border-radius: 100px;
      background: transparent;
      color: var(--text-color);
      opacity: 0.55;
      font-family: 'Poppins', sans-serif;
      font-size: 0.775rem;
      font-weight: 500;
      letter-spacing: 0.01em;
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
      white-space: nowrap;
      user-select: none;

      &:hover {
        opacity: 0.85;
        border-color: rgba(var(--border-color-rgb), 0.3);
        background: rgba(var(--border-color-rgb), 0.04);
      }

      &--active {
        opacity: 1;
        color: var(--color-blue);
        border-color: var(--color-blue);
        background: rgba(22, 91, 152, 0.06);

        &:hover {
          opacity: 1;
          background: rgba(22, 91, 152, 0.1);
          border-color: var(--color-blue);
        }
      }
    }

    .sort-pill-arrow {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      margin-right: -2px;
      animation: arrowIn 0.2s ease-out;
    }

    @keyframes arrowIn {
      from {
        opacity: 0;
        transform: translateY(3px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `]
})
export class SortDropdownComponent<T> {
  options = input.required<SortOption<T>[]>();
  selectedField = input.required<SortOption<T>['field']>();
  selectedDirection = input.required<SortDirection>();
  sortChanged = output<{ field: SortOption<T>['field']; direction: SortDirection }>();

  selectedFieldIndex = computed(() => {
    const opts = this.options();
    const field = this.selectedField();
    return opts.findIndex(o => o.field === field);
  });

  onPillClick(index: number): void {
    const opt = this.options()[index];
    if (!opt) return;

    if (this.selectedFieldIndex() === index) {
      // Same field clicked → toggle direction
      const newDir: SortDirection = this.selectedDirection() === 'asc' ? 'desc' : 'asc';
      this.sortChanged.emit({ field: opt.field, direction: newDir });
    } else {
      // New field → keep current direction
      this.sortChanged.emit({ field: opt.field, direction: this.selectedDirection() });
    }
  }
}
